import React from 'react';
import {
  ArrowUpDown,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  FileDown,
  FileSpreadsheet,
  FileText,
  Lock,
  Plus,
  Search,
  Trash2,
  Upload,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import { AutoResizeTextarea, FormattedNumberInput, SmartDateInput } from './sharedInputs';

const sortBluePrintRows = (rows, mode) => {
  const sortedRows = [...rows];
  switch (mode) {
    case 1:
      return sortedRows.sort((a, b) => (a.account_name || '').localeCompare(b.account_name || ''));
    case 2:
      return sortedRows.sort((a, b) => (a.machines || '').localeCompare(b.machines || ''));
    case 3:
      return sortedRows.sort((a, b) => (parseFloat(a.quantity) || 0) - (parseFloat(b.quantity) || 0));
    case 4:
      return sortedRows.sort((a, b) => (parseFloat(b.total_amount) || 0) - (parseFloat(a.total_amount) || 0));
    default:
      return sortedRows;
  }
};

export default function BlueprintSubmenu(props) {
  const {
    user,
    activeMenuItem,
    setActiveMenuItem,
    bluePrints,
    selectedBluePrintAccount,
    setSelectedBluePrintAccount,
    bluePrintRows,
    setBluePrintRows,
    bluePrintSortMode,
    setBluePrintSortMode,
    handleDeleteBluePrint,
    isExportModalOpen,
    setIsExportModalOpen,
    exportFilters,
    handleOpenMachineSelection,
    handleRemoveFilter,
    handleAddFilter,
    handleExportFiltered,
    handleExportAllBluePrintsExcel,
    isMachineSelectionModalOpen,
    setIsMachineSelectionModalOpen,
    handleSelectMachine,
    focusProducts,
    handleExportBluePrintExcel,
    handleExportBluePrintPDF,
    handleExportBluePrintCSV,
    handleImportBluePrintCSV,
    handleSaveBluePrint,
    handleDeleteBluePrintRow,
    setShowMachineModal,
    fetchFocusProducts,
    setCurrentMachineRowIndex,
    showMachineModal,
    setMachineSearchQuery,
    machineSearchQuery,
    currentMachineRowIndex,
    isManualInputMode,
    setIsManualInputMode,
    manualMachineName,
    setManualMachineName,
  } = props;

  const uniqueCategories = [...new Set(focusProducts.map((product) => product.category).filter(Boolean))];

  if (activeMenuItem === 'project-details-blueprint') {
    return (
      <div className="nad-modal-content content-glass rounded-2xl shadow-2xl border border-white/5 p-5 md:p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Blue Print</h2>
            <p className="text-gray-600 dark:text-gray-200">Project blueprints and layouts</p>
          </div>
          <Button className="bg-green-600 text-white hover:bg-green-700" onClick={() => setIsExportModalOpen(true)}>
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            Export Excel
          </Button>
        </div>

        {bluePrints.length === 0 ? (
          <Card className="glass border-0 shadow-lg">
            <CardContent className="py-12">
              <div className="text-center">
                <FileText className="mx-auto mb-4 h-16 w-16 text-gray-400" />
                <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">No Blue Prints Found</h3>
                <p className="text-gray-500 dark:text-gray-300">Blue prints will appear here when accounts are created in the Map section.</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {bluePrints.map((bp) => {
              const isAccessible = user.is_super_admin || bp.account_name === user.full_name;
              return (
                <Card
                  key={bp.id}
                  className={`nad-modal-content content-glass w-full rounded-xl border border-white/5 shadow-xl transition-transform duration-200 hover:-translate-y-1 hover:shadow-2xl ${isAccessible ? 'cursor-pointer' : 'cursor-not-allowed opacity-75'}`}
                  onClick={() => {
                    if (isAccessible) {
                      setSelectedBluePrintAccount(bp);
                      setBluePrintRows(sortBluePrintRows(bp.items || [], 0));
                      setBluePrintSortMode(0);
                      setActiveMenuItem('blueprint-account-details');
                    }
                  }}
                >
                  <CardHeader className="flex flex-col items-center space-y-2 pb-2 text-center">
                    <CardTitle className="w-full break-words text-center text-3xl font-bold">{bp.account_name}</CardTitle>
                  </CardHeader>
                  <CardContent className="text-left">
                    <div className="space-y-2">
                      <p className="text-base font-semibold text-gray-900 dark:text-white">Total Account: {(bp.items || []).length}</p>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">Machine Total Quantity: {(bp.items || []).reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0)}</p>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">
                        Total Amount: {new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format((bp.items || []).reduce((sum, item) => sum + (parseFloat(item.total_amount) || 0), 0))}
                      </p>
                    </div>
                    <div className="mt-4 flex items-center justify-start gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isAccessible) {
                            setSelectedBluePrintAccount(bp);
                            setBluePrintRows(sortBluePrintRows(bp.items || [], 0));
                            setBluePrintSortMode(0);
                            setActiveMenuItem('blueprint-account-details');
                          }
                        }}
                      >
                        View Blue Print
                      </Button>
                      {user.is_super_admin && (
                        <button
                          className="ml-2 text-sm font-medium text-red-500 hover:text-red-700"
                          onClick={(e) => handleDeleteBluePrint(bp.id, e)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <Dialog open={isExportModalOpen} onOpenChange={setIsExportModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Export Filtering</DialogTitle>
              <DialogDescription>Filter data by machine before exporting.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {exportFilters.map((filter, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input value={filter} readOnly placeholder="Select machine..." className="flex-1" onClick={() => handleOpenMachineSelection(index)} />
                  <Button variant="outline" size="icon" onClick={() => handleOpenMachineSelection(index)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-red-500 hover:bg-red-50 hover:text-red-700" onClick={() => handleRemoveFilter(index)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" onClick={handleAddFilter} className="w-full border-dashed">
                <Plus className="mr-2 h-4 w-4" />
                Add Filter Field
              </Button>
              <Button className="mt-4 w-full bg-blue-600 text-white hover:bg-blue-700" onClick={handleExportFiltered}>
                Export Filtered Item/s
              </Button>
            </div>
            <DialogFooter>
              <Button
                variant="default"
                className="w-full bg-green-600 text-white hover:bg-green-700"
                onClick={() => {
                  handleExportAllBluePrintsExcel(bluePrints);
                  setIsExportModalOpen(false);
                }}
              >
                Export All
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={isMachineSelectionModalOpen} onOpenChange={setIsMachineSelectionModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Select Machine</DialogTitle>
            </DialogHeader>
            <ScrollArea className="h-[300px] pr-4">
              <div className="space-y-2">
                {[...new Set(focusProducts.map((product) => product.model))].filter(Boolean).sort().map((model, index) => (
                  <div
                    key={index}
                    className="cursor-pointer rounded border p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleSelectMachine(model)}
                  >
                    {model}
                  </div>
                ))}
                {focusProducts.length === 0 && <p className="text-center text-gray-500">No machines found in Focus Products.</p>}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  if (activeMenuItem !== 'blueprint-account-details' || !selectedBluePrintAccount) {
    return null;
  }

  const canEdit = user.is_super_admin || selectedBluePrintAccount.account_name === user.full_name;

  return (
    <>
      <div className="nad-modal-content content-glass overflow-x-auto rounded-2xl shadow-2xl border border-white/5 p-6 md:p-8">
        <div className="mb-6 flex items-center">
          <Button variant="ghost" onClick={() => setActiveMenuItem('project-details-blueprint')} className="mr-4">
            <ChevronLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Button className="ml-auto mr-2 bg-green-600 hover:bg-green-700" onClick={handleExportBluePrintExcel}>
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            Export Excel
          </Button>
          <Button className="bg-red-600 hover:bg-red-700" onClick={handleExportBluePrintPDF}>
            <FileDown className="mr-2 h-4 w-4" />
            Export PDF
          </Button>
          <Button className="ml-2 bg-blue-600 hover:bg-blue-700" onClick={() => document.getElementById('blueprint-csv-upload').click()}>
            <Upload className="mr-2 h-4 w-4" />
            Import CSV
          </Button>
          <Button className="ml-2 bg-green-600 hover:bg-green-700" onClick={handleExportBluePrintCSV}>
            <FileText className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
          <input id="blueprint-csv-upload" type="file" accept=".csv" className="hidden" onChange={handleImportBluePrintCSV} />
        </div>

        <div id="blueprint-export-area" className="p-4">
          <div className="mb-4 text-center">
            <h2 className="text-xl font-bold text-gray-900 dark:text-black">{selectedBluePrintAccount.account_name}</h2>
            <p className="text-sm text-gray-600 dark:text-black">Blue Print Details</p>
          </div>
          <h3 className="mb-4 text-center text-4xl font-bold uppercase text-black dark:text-black">SALES BLUE PRINT 2026</h3>

          <div className="overflow-hidden rounded-xl border border-blue-900 shadow-sm">
            <table className="w-full table-fixed border-collapse text-xs">
              <thead>
                <tr className="bg-[#1e3a8a] text-center text-sm font-bold text-white">
                  <th className="w-[13%] border-r border-blue-800 p-2">Account name</th>
                  <th className="w-[13%] border-r border-blue-800 p-2">Account Details</th>
                  <th className="w-[13%] border-r border-blue-800 p-2">Machines</th>
                  <th className="w-[10%] border-r border-blue-800 p-2">Machine Category</th>
                  <th className="w-[6%] border-r border-blue-800 p-2">Quantity</th>
                  <th className="w-[8%] border-r border-blue-800 p-2">Amount per Qty.</th>
                  <th className="w-[8%] border-r border-blue-800 p-2">Total Amount</th>
                  <th className="w-[8%] border-r border-blue-800 p-2">Target Delivery Date</th>
                  <th className="w-[7%] border-r border-blue-800 p-2">Status</th>
                  <th className="w-[8%] border-r border-blue-800 p-2">Remarks</th>
                  <th className="w-[6%] p-2">
                    <div className="flex items-center justify-center gap-1">
                      Actions
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 hover:bg-blue-700"
                        onClick={() => {
                          const newMode = (bluePrintSortMode + 1) % 5;
                          setBluePrintSortMode(newMode);
                          setBluePrintRows(sortBluePrintRows(bluePrintRows, newMode));
                        }}
                      >
                        <ArrowUpDown className="h-3 w-3" />
                      </Button>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {bluePrintRows.map((row, index) => (
                  <tr key={index} className="border-b border-gray-200 text-center hover:bg-gray-50 dark:hover:bg-black">
                    <td className="border-r border-gray-200 p-0 align-middle break-words whitespace-normal">
                      <AutoResizeTextarea className="h-full w-full resize-none overflow-hidden bg-transparent px-1 py-2 text-center text-xs focus:bg-blue-50 focus:outline-none dark:text-black" disabled={!canEdit} rows={1} style={{ minHeight: '35px' }} value={row.account_name || ''} onChange={(e) => { const newRows = [...bluePrintRows]; newRows[index].account_name = e.target.value; setBluePrintRows(newRows); }} maxLength={50} />
                    </td>
                    <td className="border-r border-gray-200 p-0 align-middle break-words whitespace-normal">
                      <AutoResizeTextarea className="h-full w-full resize-none overflow-hidden bg-transparent px-1 py-2 text-center text-xs focus:bg-blue-50 focus:outline-none dark:text-black" disabled={!canEdit} rows={1} style={{ minHeight: '35px' }} value={row.account_details || ''} onChange={(e) => { const newRows = [...bluePrintRows]; newRows[index].account_details = e.target.value; setBluePrintRows(newRows); }} maxLength={50} />
                    </td>
                    <td className="border-r border-gray-200 p-0 align-middle break-words whitespace-normal">
                      <div className="relative flex min-h-[35px] h-full w-full items-center justify-center px-1 py-2 text-center text-xs dark:text-black">
                        {row.machines || <span className="text-gray-400">Select Machine</span>}
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="absolute inset-0 flex h-full w-full items-center justify-center bg-white/50 opacity-0 transition-opacity hover:bg-white/80 hover:opacity-100"
                            onClick={() => {
                              setCurrentMachineRowIndex(index);
                              setShowMachineModal(true);
                              if (focusProducts.length === 0) fetchFocusProducts();
                            }}
                          >
                            <Plus className="h-4 w-4 text-blue-600" />
                          </Button>
                        )}
                      </div>
                    </td>
                    <td className="border-r border-gray-200 p-0 align-middle break-words whitespace-normal">
                      <div className="relative h-full w-full">
                        <select disabled={!canEdit} className="h-full w-full appearance-none bg-transparent px-1 py-2 text-center text-xs focus:bg-blue-50 focus:outline-none dark:text-black" value={row.machine_category || ''} onChange={(e) => { const newRows = [...bluePrintRows]; newRows[index].machine_category = e.target.value; setBluePrintRows(newRows); }}>
                          <option value="">Select Category</option>
                          {uniqueCategories.map((category, categoryIndex) => (
                            <option key={categoryIndex} value={category}>{category}</option>
                          ))}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-1 top-1/2 h-3 w-3 -translate-y-1/2 transform text-gray-500 dark:text-gray-300" />
                      </div>
                    </td>
                    <td className="border-r border-gray-200 p-0 align-middle break-words whitespace-normal">
                      <input type="number" className="h-full w-full bg-transparent px-1 py-2 text-center text-xs font-medium focus:bg-blue-50 focus:outline-none dark:text-black" disabled={!canEdit} value={row.quantity || ''} onChange={(e) => { const newRows = [...bluePrintRows]; const qty = e.target.value; newRows[index].quantity = qty; const amount = parseFloat(String(newRows[index].amount_per_qty).replace(/,/g, '')); const parsedQty = parseFloat(qty); newRows[index].total_amount = !Number.isNaN(amount) && !Number.isNaN(parsedQty) ? parsedQty * amount : 0; setBluePrintRows(newRows); }} />
                    </td>
                    <td className="border-r border-gray-200 p-0 align-middle break-words whitespace-normal">
                      <FormattedNumberInput className="h-full w-full bg-transparent px-1 py-2 text-center text-xs font-medium focus:bg-blue-50 focus:outline-none dark:text-black" disabled={!canEdit} value={row.amount_per_qty || ''} onChange={(val) => { const newRows = [...bluePrintRows]; newRows[index].amount_per_qty = val; const amount = parseFloat(val); const qty = parseFloat(newRows[index].quantity); newRows[index].total_amount = !Number.isNaN(amount) && !Number.isNaN(qty) ? qty * amount : 0; setBluePrintRows(newRows); }} onBlur={() => { const val = parseFloat(row.amount_per_qty); if (!Number.isNaN(val)) { const newRows = [...bluePrintRows]; newRows[index].amount_per_qty = val; const qty = parseFloat(newRows[index].quantity); if (!Number.isNaN(qty)) newRows[index].total_amount = qty * val; setBluePrintRows(newRows); } }} />
                    </td>
                    <td className="border-r border-gray-200 p-0 align-middle break-words whitespace-normal">
                      <input className="h-full w-full bg-transparent px-1 py-2 text-center text-xs font-medium focus:bg-blue-50 focus:outline-none dark:text-black" disabled={!canEdit} value={row.total_amount ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(row.total_amount) : ''} onChange={(e) => { const newRows = [...bluePrintRows]; const val = e.target.value.replace(/,/g, ''); if (!Number.isNaN(val)) { newRows[index].total_amount = val; setBluePrintRows(newRows); } }} />
                    </td>
                    <td className="relative border-r border-gray-200 p-0 align-middle break-words whitespace-normal">
                      <SmartDateInput value={row.target_delivery_date} disabled={!canEdit} placeholder="dd-----yyyy" onChange={(e) => { const newRows = [...bluePrintRows]; newRows[index].target_delivery_date = e.target.value; setBluePrintRows(newRows); }} />
                    </td>
                    <td className="border-r border-gray-200 p-0 align-middle break-words whitespace-normal">
                      <AutoResizeTextarea className="h-full w-full resize-none overflow-hidden bg-transparent px-1 py-2 text-center text-xs focus:bg-blue-50 focus:outline-none dark:text-black" disabled={!canEdit} rows={1} style={{ minHeight: '35px' }} value={row.status || ''} onChange={(e) => { const newRows = [...bluePrintRows]; newRows[index].status = e.target.value; setBluePrintRows(newRows); }} maxLength={50} />
                    </td>
                    <td className="border-r border-gray-200 p-0 align-middle break-words whitespace-normal">
                      <AutoResizeTextarea className="h-full w-full resize-none overflow-hidden bg-transparent px-1 py-2 text-center text-xs focus:bg-blue-50 focus:outline-none dark:text-black" disabled={!canEdit} rows={1} style={{ minHeight: '35px' }} value={row.remarks || ''} onChange={(e) => { const newRows = [...bluePrintRows]; newRows[index].remarks = e.target.value; setBluePrintRows(newRows); }} maxLength={50} />
                    </td>
                    <td className="p-0 align-middle">
                      {canEdit && (
                        <div className="flex h-full items-center justify-center space-x-1">
                          <Button variant="ghost" size="sm" className="h-6 w-6 rounded-full p-0 hover:bg-green-50 dark:hover:bg-black" onClick={handleSaveBluePrint}>
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-6 w-6 rounded-full p-0 hover:bg-red-50 dark:hover:bg-black" onClick={() => handleDeleteBluePrintRow(index)}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                <tr data-blueprint-summary="true" className="bg-[#1e3a8a] text-center text-xs font-bold text-white">
                  <td className="border-r border-blue-800 p-2 text-center">{bluePrintRows.length}</td>
                  <td colSpan={3} className="border-r border-blue-800 p-2" />
                  <td className="border-r border-blue-800 p-2">{bluePrintRows.reduce((sum, row) => sum + (parseFloat(row.quantity) || 0), 0)}</td>
                  <td className="border-r border-blue-800 p-2">{new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(bluePrintRows.reduce((sum, row) => sum + (parseFloat(row.amount_per_qty) || 0), 0))}</td>
                  <td className="border-r border-blue-800 p-2">{new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(bluePrintRows.reduce((sum, row) => sum + (parseFloat(row.total_amount) || 0), 0))}</td>
                  <td colSpan={4} className="p-2" />
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {canEdit && (
          <div className="mt-4 flex justify-end">
            <Button
              className="bg-blue-900 text-white hover:bg-blue-800"
              onClick={() => {
                setBluePrintRows([
                  ...bluePrintRows,
                  {
                    account_name: '',
                    account_details: '',
                    machines: '',
                    machine_category: '',
                    quantity: '',
                    amount_per_qty: '',
                    total_amount: '',
                    target_delivery_date: '',
                    status: '',
                    remarks: '',
                  },
                ]);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Field
            </Button>
          </div>
        )}
      </div>

      <Dialog
        open={showMachineModal}
        onOpenChange={(open) => {
          setShowMachineModal(open);
          if (!open) {
            setIsManualInputMode(false);
            setManualMachineName('');
          }
        }}
      >
        <DialogContent className="flex max-h-[80vh] max-w-md flex-col">
          <DialogHeader>
            <DialogTitle>{isManualInputMode ? 'Enter Machine Name' : 'Select Machine'}</DialogTitle>
            <DialogDescription>{isManualInputMode ? 'Manually enter the machine model name.' : 'Choose a machine model from the focus products list.'}</DialogDescription>
          </DialogHeader>

          {!isManualInputMode ? (
            <>
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                <Input placeholder="Search machines..." value={machineSearchQuery} onChange={(e) => setMachineSearchQuery(e.target.value)} className="pl-10" />
              </div>
              <ScrollArea className="flex-1 pr-4">
                <div className="space-y-2">
                  {focusProducts
                    .filter((product) => product.model && product.model.toLowerCase().includes(machineSearchQuery.toLowerCase()))
                    .map((product, index) => (
                      <div
                        key={product.id || index}
                        onClick={() => {
                          if (currentMachineRowIndex !== null) {
                            const newRows = [...bluePrintRows];
                            newRows[currentMachineRowIndex].machines = product.model;
                            setBluePrintRows(newRows);
                            setShowMachineModal(false);
                            setMachineSearchQuery('');
                          }
                        }}
                        className="flex cursor-pointer items-center rounded-lg border border-transparent p-3 transition-colors hover:border-blue-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{product.model}</p>
                          <p className="text-xs text-gray-500">{product.brand} - {product.category}</p>
                        </div>
                      </div>
                    ))}
                  {focusProducts.length === 0 && <p className="py-8 text-center text-gray-500">No machines found.</p>}
                </div>
              </ScrollArea>
              <div className="mt-4 border-t border-gray-200 pt-4 dark:border-gray-700">
                <Button variant="outline" className="w-full justify-center" onClick={() => setIsManualInputMode(true)}>
                  Others (Manual Input)
                </Button>
              </div>
            </>
          ) : (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Machine Name</Label>
                <Input value={manualMachineName} onChange={(e) => setManualMachineName(e.target.value)} placeholder="Type machine name here..." autoFocus />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => { setIsManualInputMode(false); setManualMachineName(''); }}>
                  Back to List
                </Button>
                <Button
                  disabled={!manualMachineName.trim()}
                  onClick={() => {
                    if (currentMachineRowIndex !== null && manualMachineName.trim()) {
                      const newRows = [...bluePrintRows];
                      newRows[currentMachineRowIndex].machines = manualMachineName.trim();
                      setBluePrintRows(newRows);
                      setShowMachineModal(false);
                      setIsManualInputMode(false);
                      setManualMachineName('');
                    }
                  }}
                >
                  Add Machine
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
